//src/components/EditVendor.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close'; // Import Close icon
import axios from 'axios';
import env from "react-dotenv";

const EditVendor = ({ open, handleClose, fetchVendors, vendorData }) => {
  const [vendorCode, setVendorCode] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [gstNo, setGstNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    if (vendorData) {
      setVendorCode(vendorData.vendor_code || '');
      setVendorName(vendorData.vendor_name || '');
      setAddress(vendorData.address || '');
      setPhone(vendorData.phone || '');
      setCity(vendorData.city || '');
      setState(vendorData.state || '');
      setPinCode(vendorData.pin_code || '');
      setGstNo(vendorData.gst_no || '');
      setSelectedProducts(vendorData.product_list ? vendorData.product_list.split(', ') : []);
    }

    axios.get(`${process.env.REACT_APP_PJCBACKEND_URL}/api/products`)
      .then((response) => {
        setProducts(response.data);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
      });

  }, [vendorData]);

  const handleSubmit = () => {
    setLoading(true);
    const updatedVendor = {
      vendor_code: vendorCode,
      vendor_name: vendorName,
      address,
      phone,
      city,
      state,
      pin_code: pinCode,
      gst_no: gstNo,
      product_list: selectedProducts.join(', '), // Save as comma-separated values
    };

    axios
      .put(`${process.env.REACT_APP_PJCBACKEND_URL}/api/vendors/${vendorData.id}`, updatedVendor)
      .then(() => {
        fetchVendors();
        handleClose();
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error updating vendor:', error);
        setErrorMessage(error.response?.data?.message || 'An error occurred while updating the vendor.');
        setLoading(false);
      });
  };

  const handleCloseSnackbar = () => {
    setErrorMessage('');
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
            Edit Vendor
          </Typography>
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vendor Code"
              variant="outlined"
              fullWidth
              value={vendorCode}
              onChange={(e) => setVendorCode(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vendor Name"
              variant="outlined"
              fullWidth
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={products.map((product) => product.product_name)} // Map product names
              value={selectedProducts}
              onChange={(event, newValue) => setSelectedProducts(newValue)}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    key={option}
                    label={option}
                    {...getTagProps({ index })}
                    sx={{
                      backgroundColor: '#e0f7fa', // Light teal background
                      color: '#00796b',          // Dark teal text
                      '& .MuiChip-deleteIcon': {
                        color: '#004d40',        // Dark teal delete icon
                      },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Raw Material"
                  placeholder="Select Raw Material"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              variant="outlined"
              fullWidth
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              variant="outlined"
              fullWidth
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="State"
              variant="outlined"
              fullWidth
              value={state}
              onChange={(e) => setState(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Pin Code"
              variant="outlined"
              fullWidth
              type="number"
              value={pinCode}
              onChange={(e) => setPinCode(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="GST No"
              variant="outlined"
              fullWidth
              value={gstNo}
              onChange={(e) => setGstNo(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button onClick={handleClose} color="secondary" disabled={loading}
        sx={{ marginRight: 2,':hover': {
                      bgcolor: '#4863C0', // theme.palette.primary.main
                      color: 'white',
            }, }}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={loading}
        sx={{backgroundColor:'#4863A0',':hover': {
                      bgcolor: '#4863C0', // theme.palette.primary.main
                      color: 'white',
                    },}} 
        variant="contained"
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditVendor;
